import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { collectionService, PublicSharedCollection } from '../../api/collectionService';
import { useAuth } from '../../context/AuthContext';
import '../../styles/publicCollection.css';
import logo from '../../assets/logo.png';
import Footer from '../common/footer';
import { PUBLIC_APP_ORIGIN } from '../../config/publicUrls';
import { useShareData } from '../../hooks/useShareData';
import ShareHead, { ShareNotFoundHead } from '../common/ShareHead';
import { canonicalLegacyId, collectionPageText, MIN_INDEXABLE_COLLECTION_WORDS } from '../../utils/shareText';

// The /c/:token route is fed by two independent, unrelated backend systems that both happen to
// mint URLs shaped https://app.weavewords.org/c/<something>:
//   1. Share links (opaque UUID tokens) — POST /collections/:id/share, the actively-used "Share"
//      button (mobile's useShareCollection). Resolved via GET /collections/share/:token
//      (CollectionShareHandler.GetPublicByToken), which returns the narrow PublicCollection DTO
//      below — no `id` (deliberately never exposed to an anonymous viewer) and only up to 5
//      `sample_words` (not full translations/explanations — anonymous visitors get a teaser).
//   2. Legacy `is_public`-flagged collections (plain numeric collection ID) — toggled via
//      CollectionEdit.tsx's Public/Private select, and what the backend's sitemap links to for
//      SEO (sitemap_handler.go). Resolved via GET /collections/:id/public
//      (CollectionHandler.GetPublic), which returns the full `models.Collection` including every
//      `vocabulary_items` row.
// Try the token endpoint first (the primary, actively-growing sharing flow) and fall back to the
// legacy numeric one so is_public/sitemap-listed collections don't regress.
interface DisplayWord {
    key: string;
    word: string;
    translation?: string;
}

interface DisplayCollection {
    name: string;
    description: string;
    language: string;
    language_level: string;
    collection_type: string;
    item_count: number;
    words: DisplayWord[];
}

interface LoadedCollection {
    collection: DisplayCollection;
    isLegacy: boolean;
}

// Maps the share-token teaser, whether fetched or embedded server-side (utils/ssrPage.ts — only
// share-token pages embed it, legacy ones don't).
const fromShareResponse = (data: PublicSharedCollection): LoadedCollection => ({
    collection: { ...data, words: (data.sample_words ?? []).map(word => ({ key: word, word })) },
    isLegacy: false,
});

async function loadCollection(token: string): Promise<LoadedCollection> {
    try {
        return fromShareResponse(await collectionService.getPublicByShareToken(token));
    } catch (err) {
        // Fall through to the legacy lookup — on any failure, not just a 404: a legacy link must
        // keep working while the share lookup has a problem. Only for tokens the server would also
        // treat as a legacy ID, or the app would render pages the server declares 404 + noindex.
        if (canonicalLegacyId(token) === null) throw err;
    }
    const data = await collectionService.getLegacyPublic(token);
    return {
        collection: {
            ...data,
            words: (data.vocabulary_items ?? []).map(item => ({
                key: String(item.id),
                word: item.word,
                translation: item.translation?.join(' · '),
            })),
        },
        isLegacy: true,
    };
}

const PublicCollectionPage: React.FC = () => {
    const { t } = useTranslation('collection');
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const auth = useAuth();
    const isAuthenticated = !!auth?.user;
    const { data: loaded, loading, failed } = useShareData<LoadedCollection, PublicSharedCollection>(
        `/c/${token}`, () => loadCollection(token ?? ''), fromShareResponse);
    const collection = loaded?.collection ?? null;
    // The legacy numeric-id lookup (see comment above) has no matching "import into my
    // account" endpoint on the backend — only real share tokens do (CollectionShareHandler.
    // ImportByToken). "Make a Copy" is only wired up for the token flow: an anonymous
    // visitor still sees it routed to /register (harmless — registration doesn't require
    // this collection to be importable), but an already-authenticated visitor on a legacy
    // link sees no second action button at all (nothing useful to route them to) — they
    // can still use "View" to see the collection's full word list, already shown on the
    // page for this flow.
    const isLegacy = loaded?.isLegacy ?? false;
    const [importing, setImporting] = useState(false);
    const [importError, setImportError] = useState<string | null>(null);
    // Guards against making a second copy on a repeat click — once a copy has
    // succeeded for this page view, the button switches to a disabled "Copied" state
    // instead of staying clickable (see bug: "Practice" used to silently re-copy the
    // collection on every click).
    const [copied, setCopied] = useState(false);
    // Set on mount (for a returning, already-authenticated visitor) when the backend reports
    // this collection was already imported into their account — guards "Make a Copy" from
    // creating a duplicate on a repeat visit, not just a repeat click in the same page view.
    const [alreadyImported, setAlreadyImported] = useState(false);
    // Cache for the authenticated-only full word list (see handleView) — null means
    // "not fetched yet", so a repeat View click doesn't refetch.
    const [fullWords, setFullWords] = useState<DisplayWord[] | null>(null);
    const [fullWordsLoading, setFullWordsLoading] = useState(false);
    const [fullWordsError, setFullWordsError] = useState<string | null>(null);

    useEffect(() => {
        if (!isAuthenticated || isLegacy || !token || loading || !collection) return;
        let cancelled = false;
        // Through apiClient, so an expired access token is refreshed instead of silently
        // disabling this duplicate-copy guard.
        collectionService.getShareImportStatus(token)
            .then(data => {
                if (!cancelled && data.already_imported) setAlreadyImported(true);
            })
            .catch(() => {
                // Best-effort only — a failed status check just leaves "Make a Copy"
                // clickable, same as before this guard existed.
            });
        return () => { cancelled = true; };
    }, [isAuthenticated, isLegacy, token, loading, collection]);

    async function handleCopy() {
        if (!token || importing || copied || alreadyImported) return;
        setImportError(null);
        setImporting(true);
        try {
            await collectionService.importByToken(token);
            setCopied(true);
            navigate('/collections');
        } catch {
            setImportError(t('public_collection.copy_failed', 'Could not copy this collection. Please try again.'));
        } finally {
            setImporting(false);
        }
    }

    async function handleView() {
        document.getElementById('pc-sample-words')?.scrollIntoView({ behavior: 'smooth' });
        if (!isAuthenticated || isLegacy || !token) return;
        if (fullWords !== null || fullWordsLoading) return;
        setFullWordsLoading(true);
        setFullWordsError(null);
        try {
            const data = await collectionService.getFullByShareToken(token);
            setFullWords(data.words.map((w, i) => ({
                key: `${w.word}-${i}`,
                word: w.word,
                translation: w.translation?.join(' · '),
            })));
        } catch {
            setFullWordsError(t('public_collection.full_list_failed', 'Could not load the full word list. Please try again.'));
        } finally {
            setFullWordsLoading(false);
        }
    }

    if (loading) {
        return (
            <div className="pc-container">
                <div className="pc-loading">{t('public_collection.loading')}</div>
            </div>
        );
    }

    if (failed || !collection) {
        return (
            <div className="pc-container">
                <ShareNotFoundHead />
                <div className="pc-error">
                    <h2>{t('public_collection.title_unavailable')}</h2>
                    <p>{t('public_collection.not_found')}</p>
                    <button className="pc-btn" onClick={() => navigate('/')}>{t('public_collection.go_home')}</button>
                </div>
            </div>
        );
    }

    // Same formulas and JSON-LD as weavewords-api's ShareRenderHandler.collectionPage.
    const { title: pageTitle, description: pageDesc } = collectionPageText(collection);

    // /c/007 and /c/7 are the same legacy collection; the server canonicalizes to /c/7 too.
    const canonicalToken = (isLegacy && canonicalLegacyId(token ?? '')) || token;
    const collectionUrl = `${PUBLIC_APP_ORIGIN}/c/${canonicalToken}`;

    const jsonLd = [
        {
            '@context': 'https://schema.org',
            '@type': 'DefinedTermSet',
            name: collection.name,
            description: pageDesc,
            url: collectionUrl,
            inLanguage: collection.language,
        },
        {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'WeaveWords', item: `${PUBLIC_APP_ORIGIN}/` },
                { '@type': 'ListItem', position: 2, name: collection.name, item: collectionUrl },
            ],
        },
    ];

    return (
        <>
            <ShareHead
                title={pageTitle}
                description={pageDesc}
                url={collectionUrl}
                noIndex={collection.item_count < MIN_INDEXABLE_COLLECTION_WORDS}
                jsonLd={jsonLd}
            />

            <div className="pc-container">
                {!isAuthenticated && (
                    <header className="pc-header">
                        <div className="pc-header-inner">
                            <img src={logo} alt="WeaveWords" className="pc-logo" onClick={() => navigate('/')} />
                            <button className="pc-header-cta" onClick={() => navigate('/register')}>
                                {t('public_collection.start_free')}
                            </button>
                        </div>
                    </header>
                )}

                <main className="pc-main">
                    <div className="pc-hero">
                        <div className="pc-badges">
                            {collection.language && <span className="pc-badge">{collection.language}</span>}
                            {collection.language_level && <span className="pc-badge">{collection.language_level}</span>}
                            {collection.collection_type && <span className="pc-badge">{collection.collection_type}</span>}
                        </div>
                        <h1 className="pc-title">{collection.name}</h1>
                        {collection.description && <p className="pc-desc">{collection.description}</p>}
                        <p className="pc-count">{t('public_collection.words_count', { count: collection.item_count })}</p>
                        <div className="pc-actions">
                            <button className="pc-btn" onClick={handleView}>
                                {t('public_collection.view_collection', 'View')}
                            </button>
                            {isAuthenticated && !isLegacy ? (
                                <button
                                    className="pc-btn pc-btn-primary"
                                    disabled={importing || copied || alreadyImported}
                                    onClick={handleCopy}
                                >
                                    {copied
                                        ? t('public_collection.copied', 'Copied ✓')
                                        : alreadyImported
                                        ? t('public_collection.already_added', 'Already Added')
                                        : t('public_collection.make_a_copy', 'Make a Copy')}
                                </button>
                            ) : !isAuthenticated ? (
                                <button className="pc-btn pc-btn-primary" onClick={() => navigate('/register')}>
                                    {t('public_collection.make_a_copy', 'Make a Copy')}
                                </button>
                            ) : null}
                        </div>
                        {importError && <p className="pc-error">{importError}</p>}
                    </div>

                    {collection.words.length > 0 && (
                        <section className="pc-words" id="pc-sample-words">
                            <h2 className="pc-words-title">
                                {fullWords ? t('public_collection.all_words', 'All words') : t('public_collection.sample_words')}
                            </h2>
                            {fullWordsLoading && <p className="pc-loading-inline">{t('public_collection.loading_words', 'Loading...')}</p>}
                            {fullWordsError && <p className="pc-error">{fullWordsError}</p>}
                            <div className="pc-words-grid">
                                {(fullWords ?? collection.words).map(item => (
                                    <div key={item.key} className="pc-word-card">
                                        <div className="pc-word">{item.word}</div>
                                        {item.translation && (
                                            <div className="pc-translation">{item.translation}</div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {!isAuthenticated && (
                        <section className="pc-promo">
                            <h2>{t('public_collection.promo_title')}</h2>
                            <p>{t('public_collection.promo_body')}</p>
                            <button className="pc-btn pc-btn-primary" onClick={() => navigate('/register')}>
                                {t('public_collection.create_free_account')}
                            </button>
                        </section>
                    )}
                </main>

                <Footer />
            </div>
        </>
    );
};

export default PublicCollectionPage;
